import { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  getProcessValidationDetailsForVcNumber,
  getProcessValidationDetailsForSerialNumber,
  getFeatures,
} from '../redux/actionCreators/processValidation';
import { isPuneJlr } from '../constants';


const normalize = (v = '') => {
  if (!v) return '';
  return v.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\s*__\s*/g, '__').replace(/\s*_\s*/g, '_');
};

const isSuccessStatus = (status) => {
  if (!status) return false;
  const s = status.toString().toUpperCase();
  return s === 'OK' || s === 'SUCCESS' || s === 'VALIDATED' || s === 'PASS' || s === 'APPLIED';
};

const buildStatusMap = (serialResponse) => {
  const map = {};
  const machineDataArr = serialResponse?.machineData || [];
  machineDataArr.forEach((machine) => {
    const machineId = machine.machineId;
    machine.processParameters?.forEach((param) => {
      const key = `${machineId}_${normalize(param.key)}`;
      if (!map[key]) map[key] = {};
      if (param.batchNumber !== undefined && param.batchNumber !== null) {
        map[key][param.batchNumber] = { status: param.status, isSkipped: param.isSkipped };
      }
    });
  });
  return map;
};

const mergeTorqueStatus = (vcRows = [], serialResponse = {}) => {
  const statusMap = buildStatusMap(serialResponse);
  return vcRows.map((row) => {
    const mapKey = `${row.machineId}_${normalize(row.parameterKey)}`;
    const batchStatusMap = statusMap[mapKey] || {};

    const getBatchData = (i) => {
      let data = batchStatusMap[i + 1];
      if (data === undefined && i === 0) data = batchStatusMap[0];
      // When the machine reports a non-sequential batchNumber (e.g. retries use
      // batchNumber=2 but quantity=1), fall back to whichever single batch exists.
      if (data === undefined && i === 0) {
        const keys = Object.keys(batchStatusMap);
        if (keys.length === 1) data = batchStatusMap[keys[0]];
      }
      // Pune JLR: row.batchNumber (from API spread) is the definitive batch key
      if (data === undefined && isPuneJlr() && row.batchNumber != null) {
        data = batchStatusMap[row.batchNumber];
      }
      return data || null;
    };

    const getActualBatchNum = (i) => {
      if (batchStatusMap[i + 1] !== undefined) return i + 1;
      if (i === 0 && batchStatusMap[0] !== undefined) return 0;
      if (i === 0) {
        const keys = Object.keys(batchStatusMap).map(Number);
        if (keys.length === 1) return keys[0];
      }
      // Pune JLR: use the row's own batchNumber from the API
      if (isPuneJlr() && row.batchNumber != null && batchStatusMap[row.batchNumber] !== undefined) {
        return row.batchNumber;
      }
      return i + 1;
    };

    const executionStatuses = Array.from({ length: row.quantity ?? 1 }, (_, i) => {
      const data = getBatchData(i);
      if (!data) return null;
      if (data.isSkipped === true) return 'skipped';
      if (isSuccessStatus(data.status)) return true;
      if (data.status && data.status.toString().toUpperCase() === 'NOK') return false;
      return null;
    });

    const batchRawStatuses = Array.from({ length: row.quantity ?? 1 }, (_, i) => {
      const data = getBatchData(i);
      if (!data) {
        if (isPuneJlr()) {
          const fallback = row.paramStatus || row.status;
          return (fallback && fallback.toUpperCase() !== 'PENDING') ? fallback : 'PENDING';
        }
        return 'PENDING';
      }
      if (data.isSkipped === true) return 'SKIPPED';
      const statusValue = data.status;
      if (isPuneJlr() && (!statusValue || statusValue.toUpperCase() === 'PENDING')) {
        const fallback = row.paramStatus || row.status;
        if (fallback && fallback.toUpperCase() !== 'PENDING') {
          return fallback;
        }
      }
      return statusValue || 'PENDING';
    });

    const batchActualNumbers = isPuneJlr()
      ? Array.from({ length: row.quantity ?? 1 }, (_, i) => getActualBatchNum(i))
      : Array.from({ length: row.quantity ?? 1 }, (_, i) => i + 1);

    let status = 'Pending';
    if (executionStatuses.every((v) => v === true || v === 'skipped')) {
      status = executionStatuses.every((v) => v === 'skipped') ? 'SKIPPED' : 'OK';
    } else if (executionStatuses.some((v) => v === false)) {
      status = 'NOK';
    } else if (executionStatuses.some((v) => v === true || v === 'skipped')) {
      status = 'Partial';
    }

    return { ...row, status, executionStatuses, batchRawStatuses, batchActualNumbers };
  });
};

const extractRowsFromSecondApi = (serialResponse = {}) => {
  const machineDataArr = serialResponse?.machineData || [];
  const validMachines = machineDataArr.filter((machine) => machine.machineId !== 50001);
  return validMachines.flatMap((machine) =>
    (machine.processParameters || []).map((param, index) => ({
      ...param,
      parameterKey: param.key || param.parameterKey,
      quantity: param.quantity ?? 1,
      machineId: machine.machineId,
      id: `${machine.machineId}-${param.key || param.parameterKey}-${index}`,
      status: 'Pending',
      paramStatus: param.status,
    }))
  );
};

/**
 * Hook that drives machine-data polling and status computation.
 * Used by MachineDataSection (embedded inside CommonOperationScreen) and
 * by the standalone Machine page.
 *
 * @param {string|null} resolvedSerialNumber  VIN / serial number to poll for
 * @returns {{ torqueTableRows, selectedTorqueRows, handleTorqueSelectionChange,
 *             isMachineDataComplete, secondApiResponse, buildStatusMap,
 *             stopPolling, features }}
 */
const useMachineData = (resolvedSerialNumber) => {
  const dispatch = useDispatch();

  const [torqueTableRows, setTorqueTableRows] = useState([]);
  const [secondApiResponse, setSecondApiResponse] = useState(null);
  const [selectedTorqueRows, setSelectedTorqueRows] = useState([]);
  const [features, setFeatures] = useState([]);
  const [isFeaturesLoaded, setIsFeaturesLoaded] = useState(false);

  const lockedSerialNumberRef = useRef(null);
  const secondApiIntervalRef = useRef(null);
  const stopSecondApiRef = useRef(false);
  const baseRowsFromFirstApiRef = useRef([]);

  const callSecondApi = useCallback(
    (serialNumber) => {
      if (!serialNumber || serialNumber !== lockedSerialNumberRef.current) return;

      dispatch(
        getProcessValidationDetailsForSerialNumber(
          serialNumber,
          (data) => {
            setSecondApiResponse(data);
            const baseRows = baseRowsFromFirstApiRef.current;

            if (baseRows.length === 0) {
              const fallbackRows = extractRowsFromSecondApi(data);
              if (fallbackRows.length > 0) {
                baseRowsFromFirstApiRef.current = fallbackRows;
                setTorqueTableRows(mergeTorqueStatus(fallbackRows, data));
              }
              return;
            }

            setTorqueTableRows(mergeTorqueStatus(baseRows, data));
          },
          (error) => {
            console.error('Machine second API error:', error);
          }
        )
      );
    },
    [dispatch]
  );

  // Load features once
  useEffect(() => {
    dispatch(
      getFeatures(
        (data) => {
          setFeatures(data);
          setIsFeaturesLoaded(true);
        },
        () => setIsFeaturesLoaded(true)
      )
    );
  }, [dispatch]);

  // Start / restart polling whenever resolvedSerialNumber changes
  useEffect(() => {
    if (!isFeaturesLoaded) return;
    if (!resolvedSerialNumber) return;

    stopSecondApiRef.current = false;
    lockedSerialNumberRef.current = resolvedSerialNumber;

    // Reset for new VIN
    baseRowsFromFirstApiRef.current = [];
    setTorqueTableRows([]);
    setSecondApiResponse(null);
    setSelectedTorqueRows([]);

    if (secondApiIntervalRef.current) {
      clearInterval(secondApiIntervalRef.current);
      secondApiIntervalRef.current = null;
    }

    const isDisplayOverallParamsFalse =
      features.find((f) => f.name === 'DISPLAY_OVERALL_PARAMS')?.enabled === false;

    const startPolling = () => {
      callSecondApi(lockedSerialNumberRef.current);
      secondApiIntervalRef.current = setInterval(() => {
        if (stopSecondApiRef.current) return;
        callSecondApi(lockedSerialNumberRef.current);
      }, 5000);
    };

    if (isDisplayOverallParamsFalse) {
      startPolling();
    } else {
      dispatch(
        getProcessValidationDetailsForVcNumber(
          resolvedSerialNumber,
          (data) => {
            if (!Array.isArray(data)) {
              baseRowsFromFirstApiRef.current = [];
              setTorqueTableRows([]);
              setSecondApiResponse(null);
              return;
            }

            const validMachines = data.filter((m) => m.machineId !== 50001);
            const baseRows = validMachines.flatMap((machine) => {
              const params = machine.parameters || machine.processParameters || [];
              return params.map((param, index) => ({
                ...param,
                parameterKey: param.parameterKey || param.key,
                quantity: param.quantity ?? 1,
                machineId: machine.machineId,
                id: `${machine.machineId}-${param.parameterKey || param.key}-${index}`,
                status: 'Pending',
                paramStatus: param.status,
                executionStatuses: Array.from({ length: param.quantity ?? 1 }, () => null),
              }));
            });

            baseRowsFromFirstApiRef.current = baseRows;
            setTorqueTableRows(baseRows);
            startPolling();
          },
          () => {
            baseRowsFromFirstApiRef.current = [];
            setTorqueTableRows([]);
            setSecondApiResponse(null);
          }
        )
      );
    }

    return () => {
      stopSecondApiRef.current = true;
      if (secondApiIntervalRef.current) {
        clearInterval(secondApiIntervalRef.current);
        secondApiIntervalRef.current = null;
      }
    };
  }, [resolvedSerialNumber, isFeaturesLoaded, features, dispatch, callSecondApi]);

  const handleTorqueSelectionChange = useCallback((selectedIds = []) => {
    setSelectedTorqueRows(selectedIds);
  }, []);

  const stopPolling = useCallback(() => {
    stopSecondApiRef.current = true;
    if (secondApiIntervalRef.current) {
      clearInterval(secondApiIntervalRef.current);
      secondApiIntervalRef.current = null;
    }
  }, []);

  // Returns true when all machine rows are OK or SKIPPED (mark complete may proceed)
  const isMachineDataComplete = (() => {
    if (!torqueTableRows || torqueTableRows.length === 0) return true;
    if (!secondApiResponse || !secondApiResponse.machineData) return false;

    const statusMap = buildStatusMap(secondApiResponse);

    for (const row of torqueTableRows) {
      const mapKey = `${row.machineId}_${normalize(row.parameterKey)}`;
      const batchStatusMap = statusMap[mapKey] || {};
      const quantity = row.quantity || 1;

      // Pune JLR uses non-sequential batch numbers mapped in batchActualNumbers
      const batchNums = (isPuneJlr() && Array.isArray(row.batchActualNumbers))
        ? row.batchActualNumbers
        : Array.from({ length: quantity }, (_, i) => i + 1);

      for (let i = 0; i < batchNums.length; i++) {
        const batchNum = batchNums[i];
        let batchData = batchStatusMap[batchNum];
        if (batchData === undefined && i === 0) {
          batchData = batchStatusMap[0];
        }
        if (batchData === undefined && i === 0 && !isPuneJlr()) {
          const keys = Object.keys(batchStatusMap);
          if (keys.length === 1) batchData = batchStatusMap[keys[0]];
        }
        if (!batchData) return false;

        const status = (batchData.status || '').toUpperCase().trim();
        if (status === 'OK') continue;
        if ((status === 'NOK') && batchData.isSkipped !== true) return false;
      }
    }

    return true;
  })();

  return {
    torqueTableRows,
    selectedTorqueRows,
    handleTorqueSelectionChange,
    isMachineDataComplete,
    secondApiResponse,
    buildStatusMap: () => buildStatusMap(secondApiResponse),
    stopPolling,
    features,
    callSecondApi: () => callSecondApi(lockedSerialNumberRef.current),
  };
};

export default useMachineData;
